// app/javascript/controllers/dropzone_controller.js
import { Controller } from "@hotwired/stimulus";
import { EmailParser } from "../utils/email_parser";

export default class extends Controller {
  static targets = ["zone", "input", "fileInput", "filesPreview"];

  connect() {
    this.dragCounter = 0;
    this.selectedFiles = [];
    this.updateFilesPreview();
  }

  // Click to open file browser
  clickZone(event) {
    // Don't trigger if clicking the file input itself
    if (event.target === this.fileInputTarget) return;
    // Don't trigger if clicking on files preview area or remove buttons
    if (event.target.closest('.drop-zone-files-preview')) return;
    if (event.target.closest('.file-remove-btn')) return;
    this.fileInputTarget.click();
  }

  // Handle file selection from input
  fileSelected(event) {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      this.addFiles(files);
      this.handleDroppedFiles(files);
    }
  }

  dragenter(event) {
    event.preventDefault();
    this.dragCounter++;
    this.zoneTarget.classList.add("drag-over");
  }

  dragleave(event) {
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.zoneTarget.classList.remove("drag-over");
    }
  }

  dragover(event) {
    event.preventDefault();
  }

  drop(event) {
    event.preventDefault();
    this.dragCounter = 0;
    this.zoneTarget.classList.remove("drag-over");

    const files = event.dataTransfer.files;
    const text = event.dataTransfer.getData("text/plain");
    const html = event.dataTransfer.getData("text/html");

    // Handle dropped text (like emails or copied content)
    if (text || html) {
      this.handleDroppedText(text, html);
      return;
    }

    // Handle dropped files
    if (files.length > 0) {
      this.handleDroppedFiles(files);
    }
  }

  handleDroppedText(text, html = null) {
    if (!this.hasInputTarget) return;

    // Check if input target is a trix-editor
    if (this.inputTarget.tagName === "TRIX-EDITOR") {
      const editor = this.inputTarget.editor;
      
      // Add separator if there's existing content
      if (editor.getDocument().toString().trim().length > 0) {
        editor.insertLineBreak();
        editor.insertHTML("<hr>");
        editor.insertLineBreak();
      }
      
      // Insert HTML if available, otherwise plain text
      if (html) {
        editor.insertHTML(html);
      } else {
        editor.insertString(text);
      }
      
      this.inputTarget.focus();
    } else {
      // Regular textarea/input
      const currentContent = this.inputTarget.value;
      const separator = currentContent ? "\n\n---\n\n" : "";
      this.inputTarget.value = currentContent + separator + (text || this.extractTextFromHtml(html));
      this.inputTarget.focus();
    }
  }

  handleDroppedFiles(files) {
    Array.from(files).forEach(file => {
      // Handle .eml email files specially - extract content and add to editor
      if (file.name.endsWith(".eml")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.handleEmailFile(e.target.result);
        };
        reader.readAsText(file);
        // Also add as attachment
        this.addFiles([file]);
      } else if (file.type.startsWith("text/") || file.name.endsWith(".txt")) {
        // Handle regular text files - extract content and add to editor
        const reader = new FileReader();
        reader.onload = (e) => {
          this.handleDroppedText(e.target.result);
        };
        reader.readAsText(file);
        // Also add as attachment
        this.addFiles([file]);
      } else {
        // Handle other file types as attachments only
        this.addFiles([file]);
      }
    });
  }

  addFiles(files) {
    // Add files to our selected files array
    files.forEach(file => {
      if (!this.selectedFiles.find(f => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified)) {
        this.selectedFiles.push(file);
      }
    });

    // Update the file input with all selected files
    this.updateFileInput();
    this.updateFilesPreview();
  }

  removeFileByIndex(index) {
    this.selectedFiles.splice(index, 1);
    this.updateFileInput();
    this.updateFilesPreview();
  }

  updateFileInput() {
    // Create a new DataTransfer object to hold our files
    const dataTransfer = new DataTransfer();
    this.selectedFiles.forEach(file => {
      dataTransfer.items.add(file);
    });
    
    // Update the file input
    this.fileInputTarget.files = dataTransfer.files;
  }

  updateFilesPreview() {
    if (!this.hasFilesPreviewTarget) return;

    if (this.selectedFiles.length === 0) {
      this.filesPreviewTarget.innerHTML = "";
      this.filesPreviewTarget.style.display = "none";
      return;
    }

    this.filesPreviewTarget.style.display = "block";
    this.filesPreviewTarget.innerHTML = `
      <div class="files-preview-container">
        <div class="files-preview-header">
          <span class="files-count">${this.selectedFiles.length} file${this.selectedFiles.length > 1 ? 's' : ''} selected</span>
        </div>
        <div class="files-preview-list">
          ${this.selectedFiles.map((file, index) => `
            <div class="file-preview-item">
              <div class="file-preview-icon">
                <i class="bi ${this.getFileIcon(file)}"></i>
              </div>
              <div class="file-preview-info">
                <span class="file-name">${this.truncateFileName(file.name, 30)}</span>
                <span class="file-size">${this.formatFileSize(file.size)}</span>
              </div>
              <button type="button" class="file-remove-btn" data-action="click->dropzone#removeFile" data-index="${index}" title="Remove file">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  getFileIcon(file) {
    if (file.type.startsWith('image/')) {
      return 'bi-image';
    } else if (file.type === 'application/pdf') {
      return 'bi-file-earmark-pdf';
    } else if (file.type.startsWith('text/')) {
      return 'bi-file-earmark-text';
    } else if (file.name.endsWith('.eml')) {
      return 'bi-envelope';
    } else {
      return 'bi-file-earmark';
    }
  }

  truncateFileName(name, maxLength) {
    if (name.length <= maxLength) return name;
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.length - extension.length - 1);
    const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 4);
    return `${truncated}...${extension}`;
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  removeFile(event) {
    event.preventDefault();
    event.stopPropagation();
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.removeFileByIndex(index);
  }

  handleEmailFile(rawEmail) {
    if (!this.hasInputTarget) return;

    try {
      const parser = new EmailParser(rawEmail);
      const formattedHTML = parser.toFormattedHTML();

      if (this.inputTarget.tagName === "TRIX-EDITOR") {
        const editor = this.inputTarget.editor;
        
        // Add separator if there's existing content
        if (editor.getDocument().toString().trim().length > 0) {
          editor.insertLineBreak();
          editor.insertHTML("<hr>");
          editor.insertLineBreak();
        }
        
        // Insert the formatted email
        editor.insertHTML(formattedHTML);
        this.inputTarget.focus();
      } else {
        // Fallback for regular textarea
        const summary = parser.toSummary();
        const plainText = `
From: ${summary.from.display}
To: ${summary.to.display}
Date: ${summary.formattedDate}
Subject: ${summary.subject}

${parser.body.plain || parser.cleanContent}
`.trim();
        
        const currentContent = this.inputTarget.value;
        const separator = currentContent ? "\n\n---\n\n" : "";
        this.inputTarget.value = currentContent + separator + plainText;
        this.inputTarget.focus();
      }
    } catch (error) {
      console.error("Error parsing email:", error);
      // Fallback to raw content if parsing fails
      this.handleDroppedText(rawEmail);
    }
  }

  extractTextFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }
}
